/**
 * BirthdayNeonBash — Modern interactive birthday invitation with 3D effects.
 *
 * Features: Neon aesthetic, 3D animations, interactive confetti, particle effects,
 * guest wishes board, memory carousel, countdown timer with Marathi/English toggle.
 *
 * id: `birthday-neon-bash`
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { Hero3DSection } from './sections/Hero3DSection';
import { CountdownNeonSection } from './sections/CountdownNeonSection';
import { MemoryCarouselSection } from './sections/MemoryCarouselSection';
import { ParticleBackground } from './sections/ParticleBackground';
import { WishesInteractiveSection } from './sections/WishesInteractiveSection';
import { GuestPhotosGridSection } from './sections/GuestPhotosGridSection';
import { VenueNeonSection } from './sections/VenueNeonSection';
import { RSVPNeonSection } from './sections/RSVPNeonSection';
import './neon-bash.css';

const META: TemplateMetadata = {
  id: 'birthday-neon-bash',
  name: 'Birthday Neon Bash',
  occasionType: 'BIRTHDAY',
  description: 'Modern interactive birthday invitation with 3D animations, neon effects, particle systems, and interactive guest engagement features.',
  isPremium: true,
  tags: ['birthday', 'modern', 'neon', 'interactive', '3d', 'animated', 'particles', 'premium'],
  colorScheme: {
    primary: '#00FFD1',
    accent: '#FF006E',
    bg: '#0A0E27',
  },
};

interface NeonBashData {
  birthday: {
    name: string;
    nameMr?: string;
    age: number;
    photo?: string;
    title?: string;
    titleMr?: string;
  };
  eventDate: string;
  eventTime: string;
  eventVenue: string;
  eventVenueMr?: string;
  eventDescription?: string;
  eventDescriptionMr?: string;
  photos: string[];
  photoCaptions?: string[];
  photoCaptionsMr?: string[];
  theme?: 'neon' | 'dark' | 'gradient';
}

export function BirthdayNeonBash({ data }: { data: InvitationData }) {
  const customDataAny = (data.customData || {}) as any;
  const bdayData = customDataAny.birthday || {};
  const [language, setLanguage] = useState<'en' | 'mr'>('en');
  const [wishesCount, setWishesCount] = useState(0);
  const [showWishes, setShowWishes] = useState(false);
  const particleRef = useRef<HTMLDivElement>(null);

  const content = useMemo(() => {
    const name = bdayData.name || 'Birthday Star';
    const nameMr = bdayData.nameMr || name;
    const age = bdayData.age || 25;
    const eventDate = data.eventDate ? new Date(data.eventDate) : new Date();
    const venueName = data.eventVenue || 'Party Venue';
    const venueNameMr = bdayData.eventVenueMr || venueName;

    return {
      birthday: {
        name,
        nameMr,
        age,
        photo: bdayData.photo || data.mediaUrls?.[0] || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=500',
        title: bdayData.title || 'Turning',
        titleMr: bdayData.titleMr || 'जन्मदिन',
      },
      event: {
        date: eventDate,
        dateStr: eventDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
        dateMr: eventDate.toLocaleDateString('mr-IN', { weekday: 'long', month: 'short', day: 'numeric' }),
        time: data.eventTime || '7:00 PM',
        venue: venueName,
        venueMr: venueNameMr,
        description: bdayData.eventDescription || 'Join us for an unforgettable celebration',
        descriptionMr: bdayData.eventDescriptionMr || 'आमच्याबरोबर असंख्य उत्सवामध्ये सामील व्हा',
      },
      photos: data.mediaUrls?.slice(0, 6) || [],
      photoCaptions: bdayData.photoCaptions || ['Memory 1', 'Memory 2', 'Memory 3', 'Memory 4', 'Memory 5', 'Memory 6'],
      photoCaptionsMr: bdayData.photoCaptionsMr || [],
      invitationId: data.invitationId ?? data.id ?? null,
      isLive: data.status === 'PUBLISHED' || data.status === 'PROCESSING',
    };
  }, [data, bdayData]);

  const triggerConfetti = () => {
    if (particleRef.current) {
      const confettiPieces = 50;
      for (let i = 0; i < confettiPieces; i++) {
        const piece = document.createElement('div');
        piece.className = 'confetti-piece';
        piece.style.left = Math.random() * 100 + '%';
        piece.style.backgroundColor = ['#00FFD1', '#FF006E', '#FFD60A', '#00D9FF'][Math.floor(Math.random() * 4)];
        piece.style.animation = `confetti-fall ${2 + Math.random() * 1}s linear forwards`;
        particleRef.current?.appendChild(piece);
        setTimeout(() => piece.remove(), 3000);
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(triggerConfetti, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="birthday-neon-bash min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-black text-white overflow-hidden">
      {/* Particle Background */}
      <ParticleBackground />

      {/* Confetti Container */}
      <div ref={particleRef} className="confetti-container fixed inset-0 pointer-events-none z-0" />

      {/* Language Toggle */}
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        <button
          onClick={() => setLanguage('en')}
          className={`px-4 py-2 rounded-full font-bold transition-all ${
            language === 'en'
              ? 'bg-cyan-500 text-black shadow-lg shadow-cyan-500'
              : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
          }`}
        >
          EN
        </button>
        <button
          onClick={() => setLanguage('mr')}
          className={`px-4 py-2 rounded-full font-bold transition-all ${
            language === 'mr'
              ? 'bg-pink-500 text-white shadow-lg shadow-pink-500'
              : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
          }`}
        >
          मराठी
        </button>
      </div>

      {/* Main Content */}
      <main className="relative z-10">
        {/* Hero 3D Section */}
        <Hero3DSection content={content} language={language} onCelebrate={triggerConfetti} />

        {/* Countdown Neon */}
        <CountdownNeonSection eventDate={content.event.date} language={language} />

        {/* Memory Carousel */}
        {content.photos.length > 0 && (
          <MemoryCarouselSection
            photos={content.photos}
            captions={language === 'mr' ? content.photoCaptionsMr : content.photoCaptions}
            language={language}
          />
        )}

        {/* Guest Photos Grid */}
        <GuestPhotosGridSection language={language} />

        {/* Venue Neon */}
        <VenueNeonSection venue={content.event} language={language} />

        {/* Wishes Interactive Wall */}
        <WishesInteractiveSection
          invitationId={content.invitationId}
          isLive={content.isLive}
          language={language}
          onWishAdded={() => setWishesCount(w => w + 1)}
          totalWishes={wishesCount}
        />

        {/* RSVP Neon Section */}
        <RSVPNeonSection invitationId={content.invitationId} isLive={content.isLive} language={language} />
      </main>

      {/* Floating Elements */}
      <div className="fixed bottom-4 left-4 z-50 text-xs text-gray-500">
        Wishes: {wishesCount}
      </div>
    </div>
  );
}

registry.register({ metadata: META, component: BirthdayNeonBash });
